// src/components/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';

const Header = () => {
  const { itemCount } = useCart();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/products', label: 'Products' },
    { path: '/categories', label: 'Categories' },
    { path: '/category/weekly-special', label: 'Weekly Specials 🏷️' },
    { path: '/about', label: 'About' },
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking outside (optional enhancement)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('header')) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  // Scroll to top when navigating
  const handleNavClick = (path) => {
    setIsMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header style={{
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid var(--border)',
      boxShadow: isScrolled ? 'var(--shadow-md)' : 'var(--shadow-sm)',
      transition: 'all 0.3s ease'
    }}>
      <div className="container" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-md) var(--space-lg)',
        height: '4rem',
        '@media (min-width: 768px)': {
          padding: 'var(--space-md) var(--space-xl)'
        }
      }}>
        {/* Logo */}
        <Link
          to="/"
          onClick={() => handleNavClick('/')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            textDecoration: 'none'
          }}
        >
          <div style={{
            width: '2.5rem',
            height: '2.5rem',
            background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
            borderRadius: 'var(--radius-lg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '1.5rem',
            transition: 'transform 0.2s ease'
          }}>
            🍀
          </div>
          <div>
            <div style={{
              fontSize: '1.25rem',
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              '@media (min-width: 768px)': {
                fontSize: '1.5rem'
              }
            }}>
              InAndOutDC
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: 'var(--text-light)',
              display: 'none',
              '@media (min-width: 768px)': {
                display: 'block'
              }
            }}>
              Premium Delivery
            </div>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav style={{
          display: 'none',
          gap: 'var(--space-xl)',
          alignItems: 'center',
          '@media (min-width: 768px)': {
            display: 'flex'
          }
        }}>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => handleNavClick(link.path)}
              style={{
                color: location.pathname === link.path ? 'var(--primary)' : 'var(--text)',
                textDecoration: 'none',
                fontWeight: location.pathname === link.path ? '600' : '500',
                position: 'relative',
                padding: 'var(--space-sm) 0',
                fontSize: 'var(--font-md)',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: 'var(--primary)'
                }
              }}
            >
              {link.label}
              {location.pathname === link.path && (
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  backgroundColor: 'var(--primary)',
                  borderRadius: '1px',
                  animation: 'slideIn 0.3s ease'
                }} />
              )}
            </Link>
          ))}
        </nav>

        {/* Cart & Mobile Menu */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-md)'
        }}>
          <Link
            to="/cart"
            onClick={() => handleNavClick('/cart')}
            style={{
              position: 'relative',
              padding: 'var(--space-sm)',
              textDecoration: 'none',
              color: 'var(--text)',
              borderRadius: 'var(--radius-md)',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)'
              }
            }}
            aria-label={`Shopping cart with ${itemCount} items`}
          >
            <span style={{
              fontSize: '1.5rem',
              display: 'inline-block',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.1)'
              }
            }}>🛒</span>
            {itemCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '0',
                right: '0',
                backgroundColor: 'var(--primary)',
                color: 'white',
                borderRadius: '50%',
                minWidth: '1.25rem',
                height: '1.25rem',
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                padding: '0 4px',
                animation: itemCount > 0 ? 'bounce 0.5s' : 'none'
              }}>
                {itemCount > 99 ? '99+' : itemCount}
              </span>
            )}
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{
              background: 'none',
              border: 'none',
              padding: 'var(--space-sm)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-md)',
              transition: 'background-color 0.2s ease',
              '@media (min-width: 768px)': {
                display: 'none'
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)'
              }
            }}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            <div style={{
              width: '1.5rem',
              height: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-around'
            }}>
              <div style={{
                height: '2px',
                backgroundColor: 'var(--text)',
                borderRadius: '1px',
                transform: isMenuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none',
                transition: 'transform 0.3s ease',
                transformOrigin: 'center'
              }} />
              <div style={{
                height: '2px',
                backgroundColor: 'var(--text)',
                borderRadius: '1px',
                opacity: isMenuOpen ? 0 : 1,
                transition: 'opacity 0.3s ease'
              }} />
              <div style={{
                height: '2px',
                backgroundColor: 'var(--text)',
                borderRadius: '1px',
                transform: isMenuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none',
                transition: 'transform 0.3s ease',
                transformOrigin: 'center'
              }} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div style={{
          backgroundColor: 'white',
          borderTop: '1px solid var(--border)',
          animation: 'slideDown 0.3s ease',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          '@media (min-width: 768px)': {
            display: 'none'
          }
        }}>
          <div style={{
            padding: 'var(--space-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-xs)'
          }}>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => handleNavClick(link.path)}
                style={{
                  padding: 'var(--space-lg)',
                  color: location.pathname === link.path ? 'var(--primary)' : 'var(--text)',
                  textDecoration: 'none',
                  fontWeight: location.pathname === link.path ? '600' : '500',
                  backgroundColor: location.pathname === link.path ? 'rgba(5, 150, 105, 0.1)' : 'transparent',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all 0.2s ease',
                  fontSize: 'var(--font-lg)',
                  '&:hover': {
                    backgroundColor: 'rgba(5, 150, 105, 0.05)',
                    transform: 'translateX(4px)'
                  },
                  '&:active': {
                    backgroundColor: 'rgba(5, 150, 105, 0.15)'
                  }
                }}
              >
                {link.label}
              </Link>
            ))}
            <div style={{
              padding: 'var(--space-xl) var(--space-lg)',
              color: 'var(--text-light)',
              fontSize: 'var(--font-sm)',
              borderTop: '1px solid var(--border)',
              marginTop: 'var(--space-sm)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              backgroundColor: 'var(--gray-50)',
              borderRadius: 'var(--radius-md)'
            }}>
              <span style={{ fontSize: '1.25rem' }}>📞</span>

              <div>
                <div style={{ fontWeight: '600', color: 'var(--text)' }}>
                  Call/Text Us Now
                </div>
                <a href="tel:2024769173" className="phone-link">
                  (202) 476-9173
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;